import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Trash2, BookOpen, Zap, Layers, FileQuestion, TrendingUp, Sparkles, RefreshCw, Share2, MessagesSquare, FileText, Brain } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toPng } from "html-to-image";
import FlashcardReview from "./FlashcardReview";
import QuizRunner from "./QuizRunner";
import AdSlot from "@/components/AdSlot";
import { ShareCard } from "@/components/ShareCard";
import TutorChat from "@/components/TutorChat";
import ExamPanel from "@/components/ExamPanel";
import PublishWizard from "@/components/PublishWizard";
import GenerationStatus from "@/components/GenerationStatus";
import NotesPanel from "@/components/NotesPanel";
import RenameTitle from "@/components/RenameTitle";
import RarityBadge from "@/components/RarityBadge";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { useTutorial } from "@/lib/tutorial";
import { toast } from "sonner";

const TABS = [
  { key: "notes", label: "Notes", icon: BookOpen, color: "#7C5CFF" },
  { key: "summary", label: "Summary", icon: Layers, color: "#5AC8FA" },
  { key: "flashcards", label: "Cards", icon: Zap, color: "#B8A4FF" },
  { key: "quiz", label: "Quiz", icon: FileQuestion, color: "#7C5CFF" },
  { key: "tutor", label: "Tutor", icon: MessagesSquare, color: "#B8A4FF" },
  { key: "exam_mode", label: "Exam", icon: FileText, color: "#FCA5A5" },
  { key: "glossary", label: "Glossary", icon: BookOpen, color: "#5AC8FA" },
  { key: "exam", label: "Forecast", icon: TrendingUp, color: "#B8A4FF" },
];

const STATUS = {
  not_started: { label: "Fresh", color: "#71717A" },
  in_progress: { label: "In progress", color: "#7C5CFF" },
  reviewed: { label: "Reviewed", color: "#B8A4FF" },
  mastered: { label: "Mastered", color: "#5AC8FA" },
};

export default function StudyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [sc, setSc] = useState(null);
  const [tab, setTab] = useState("notes");
  const [summaryLevel, setSummaryLevel] = useState("standard");
  const [publishing, setPublishing] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const thumbRef = useRef(null);

  const load = () => api.get(`/subcourses/${id}`).then((r) => setSc(r.data));
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!sc) return;
    if (sc.generation_status && !["ready", "failed"].includes(sc.generation_status)) {
      const t = setInterval(load, 3000);
      return () => clearInterval(t);
    }
  }, [sc?.generation_status]);

  const { startIfNew } = useTutorial();
  useEffect(() => {
    if (sc && (!sc.generation_status || sc.generation_status === "ready")) {
      const t = setTimeout(() => startIfNew("study"), 1100);
      return () => clearTimeout(t);
    }
  }, [sc?.generation_status]);

  const onDelete = async () => {
    if (!confirm("Delete this study?")) return;
    await api.delete(`/subcourses/${id}`);
    toast.success("Deleted");
    nav("/app/studies");
  };

  const onReroll = async () => {
    if (!confirm("Spend 40 gems to regenerate this study with smarter AI?")) return;
    try {
      await api.post("/gems/redeem/ai-reroll", { sc_id: id });
      toast.success("Re-rolling — fresh content in ~60s");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't re-roll");
    }
  };

  const onSharePack = () => setPublishOpen(true);

  const generateThumbB64 = async () => {
    if (!thumbRef.current) return null;
    try {
      const dataUrl = await toPng(thumbRef.current, {
        cacheBust: true, pixelRatio: 1, backgroundColor: "#0A0A0B",
      });
      return dataUrl.split(",", 2)[1] || null;
    } catch {
      return null;
    }
  };

  if (!sc) {
    return (
      <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-14">
        <div className="h-5 w-24 shimmer rounded mb-3" />
        <div className="h-12 w-2/3 shimmer rounded" />
      </div>
    );
  }

  if (sc.generation_status && sc.generation_status !== "ready") {
    return (
      <div className="max-w-[720px] mx-auto px-5 md:px-8 py-10 md:py-14">
        <button
          onClick={() => nav(-1)}
          className="text-[13px] font-medium text-[#B0AEBC] hover:text-white flex items-center gap-1 mb-8"
          data-testid="sc-back-btn-gen"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="eyebrow">{sc.generation_status === "failed" ? "Not generated" : "Generating"}</div>
        <h1 className="display-2 mt-2 mb-8">{sc.title}</h1>
        <GenerationStatus sc={sc} onRetried={load} />

        <div className="mt-6">
          <AdSlot
            slot={process.env.REACT_APP_ADSENSE_SLOT_GEN}
            className="rounded-2xl"
            testId="ad-during-generation"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-10 md:py-14">
      {/* Off-screen thumbnail used for OG preview on share */}
      <div
        aria-hidden
        style={{
          position: "fixed",
          left: "-99999px",
          top: 0,
          width: 1080,
          height: 1080,
          pointerEvents: "none",
        }}
      >
        <ShareCard innerRef={thumbRef} type="pack" data={{ value: sc.title }} user={user} />
      </div>

      <button
        onClick={() => nav(-1)}
        className="text-[13px] font-medium text-[#B0AEBC] hover:text-white flex items-center gap-1 mb-8"
        data-testid="sc-back-btn"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-10"
      >
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
            style={{ background: `${STATUS[sc.status].color}18`, color: STATUS[sc.status].color }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS[sc.status].color }} />
            {STATUS[sc.status].label}
          </span>
          <span className="chip chip-purple">Difficulty {sc.difficulty}/5</span>
          <span className="chip chip-teal">~{sc.estimated_study_time_minutes} min</span>
          {sc.subject_name && sc.subject_name !== "General" && (
            <span className="chip chip-blue" data-testid="sc-subject-chip">{sc.subject_name}</span>
          )}
          {sc.rarity && <RarityBadge rarity={sc.rarity} size="md" testid="sc-rarity-badge" />}
        </div>
        <div className="flex items-start justify-between gap-4">
          <RenameTitle sc={sc} onSaved={load} />
          <div className="flex items-center gap-1">
            <button
              onClick={onSharePack}
              data-testid="sc-share-pack-btn"
              title="Publish to community marketplace"
              className="btn-icon hover:text-[#B8A4FF] disabled:opacity-50"
            >
              <Share2 size={15} />
            </button>
            <button
              onClick={onReroll}
              data-testid="sc-reroll-btn"
              title="Re-roll with smarter AI (40 gems)"
              className="btn-icon hover:text-[#B8A4FF]"
            >
              <RefreshCw size={15} />
            </button>
            <button
              onClick={onDelete}
              data-testid="sc-delete-btn"
              className="btn-icon hover:text-[#B8A4FF]"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-4">
          {(sc.tags || []).map((t, i) => (
            <span key={t} className={`chip ${["chip-blue", "chip-purple", "chip-orange", "chip-green"][i % 4]}`}>{t}</span>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
          <Metric tone="green" label="Mastery" value={`${sc.mastery_score || 0}%`} bar={sc.mastery_score || 0} />
          <Metric tone="purple" label="Cards" value={sc.flashcard_count || 0} />
          <Metric tone="orange" label="Due now" value={sc.flashcards_due || 0} highlight={sc.flashcards_due > 0} />
          <Metric tone="blue" label="Quiz best" value={`${sc.quiz_best_score || 0}%`} />
        </div>

        <Link
          to={`/app/studies/${id}/techniques`}
          data-testid="sc-techniques-banner"
          className="group flex items-center gap-3.5 mt-4 rounded-2xl px-5 py-4 transition-colors"
          style={{ background: "rgba(124,92,255,0.10)", border: "1px solid rgba(124,92,255,0.28)" }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(124,92,255,0.18)" }}>
            <Brain size={20} className="text-[#B8A4FF]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[14.5px] font-semibold">Study Techniques</p>
            <p className="text-[12.5px] text-[#B0AEBC]">Practice with Feynman, Blurting & SQ3R — proven active-recall methods.</p>
          </div>
          <ArrowLeft size={16} className="rotate-180 text-[#B8A4FF] group-hover:translate-x-0.5 transition-transform shrink-0" />
        </Link>
      </motion.header>

      {/* Tabs */}
      <div className="mb-8 sticky top-[56px] z-20 backdrop-blur-md py-3 -mx-5 px-5 md:-mx-8 md:px-8 border-b border-white/5"
        style={{ background: theme === "light" ? "rgba(244,245,250,0.85)" : "rgba(10,10,11,0.85)" }}>
        <div className="overflow-x-auto no-scrollbar">
          <div className="pill-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                data-state={tab === t.key ? "active" : "inactive"}
                className="pill-tab whitespace-nowrap relative"
              >
                {tab === t.key && (
                  <motion.span
                    layoutId="study-tab-pill"
                    className="absolute inset-0 rounded-full"
                    style={{ background: "linear-gradient(135deg, rgba(124,92,255,0.35), rgba(90,200,250,0.20))", boxShadow: "0 0 0 1px rgba(124,92,255,0.4), 0 0 24px -4px rgba(124,92,255,0.6)" }}
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
                <span className="relative flex items-center gap-1.5">
                  <t.icon size={12} className={tab === t.key ? "text-white" : ""} />
                  {t.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        >
          {tab === "notes" && <NotesPanel sc={sc} onSaved={load} />}

          {tab === "summary" && (
            <div>
              <div className="pill-tabs mb-7">
                {["brief", "standard", "full"].map((l) => (
                  <button
                    key={l}
                    onClick={() => setSummaryLevel(l)}
                    data-testid={`summary-${l}`}
                    data-state={summaryLevel === l ? "active" : "inactive"}
                    className="pill-tab capitalize relative"
                  >
                    {summaryLevel === l && (
                      <motion.span
                        layoutId="summary-pill"
                        className="absolute inset-0 rounded-full"
                        style={{ background: "linear-gradient(135deg, rgba(124,92,255,0.35), rgba(90,200,250,0.20))", boxShadow: "0 0 0 1px rgba(124,92,255,0.4), 0 0 24px -4px rgba(124,92,255,0.6)" }}
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                    <span className="relative">{l}</span>
                  </button>
                ))}
              </div>
              <article className="prose-apple">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{sc[`summary_${summaryLevel}`] || "No summary."}</ReactMarkdown>
              </article>
            </div>
          )}

          {tab === "flashcards" && <FlashcardReview subcourseId={id} onComplete={load} />}
          {tab === "quiz" && <QuizRunner subcourseId={id} onComplete={load} />}
          {tab === "tutor" && <TutorChat scId={id} />}
          {tab === "exam_mode" && <ExamPanel scId={id} planTier={user?.plan} proPass={user?.pro_pass_until} />}

          {tab === "glossary" && (
            <div className="grid sm:grid-cols-2 gap-4">
              {(sc.glossary || []).map((g, i) => {
                const tones = ["chip-blue", "chip-purple", "chip-orange", "chip-green", "chip-teal", "chip-pink"];
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    whileHover={{ y: -3 }}
                    className="surface p-5"
                    data-testid={`glossary-term-${i}`}
                  >
                    <h4 className="text-[16px] font-semibold mb-2">
                      <span className="grad-text">{g.term}</span>
                    </h4>
                    <p className="text-[14px] text-[#B0AEBC] leading-relaxed">{g.definition}</p>
                    {g.related_terms?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {g.related_terms.map((r, j) => (
                          <span key={r} className={`chip ${tones[(i + j) % tones.length]}`}>{r}</span>
                        ))}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}

          {tab === "exam" && <Forecast items={sc.likely_exam_questions || []} />}
        </motion.div>
      </AnimatePresence>

      {publishOpen && (
        <PublishWizard
          scId={id}
          study={sc}
          onClose={() => setPublishOpen(false)}
          onPublished={() => { load(); }}
          getThumbnailBase64={generateThumbB64}
        />
      )}
    </div>
  );
}

const Metric = ({ tone, label, value, bar, highlight }) => {
  const { theme } = useTheme();
  const tones = {
    blue: "tile-blue", purple: "tile-purple", orange: "tile-orange",
    green: "tile-green", pink: "tile-pink",
  };
  const colors = { blue: "#B8A4FF", purple: "#B8A4FF", orange: "#B8A4FF", green: "#5AC8FA", pink: "#B8A4FF" };
  const baseValueColor = theme === "light" ? "#1A1A22" : "#F4F4F5";
  return (
    <div className={`${tones[tone]} rounded-2xl p-4`}>
      <div className="text-[11px] font-semibold text-[#B0AEBC] mb-1.5 uppercase tracking-wider">{label}</div>
      <div
        className="text-[24px] font-bold tracking-tight tabular-nums"
        style={{ color: highlight ? colors[tone] : baseValueColor }}
      >
        {value}
      </div>
      {typeof bar === "number" && (
        <div className="mt-2 h-1.5 progress-track">
          <motion.div
            className="h-full progress-fill"
            initial={{ width: 0 }}
            animate={{ width: `${bar}%` }}
            transition={{ duration: 0.9, delay: 0.2 }}
          />
        </div>
      )}
    </div>
  );
};

const Forecast = ({ items }) => {
  const [open, setOpen] = useState({});
  if (items.length === 0) return <p className="text-[#8E8B99]">No predictions.</p>;
  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <TrendingUp size={18} className="text-[#B8A4FF]" />
        <h3 className="text-[18px] font-semibold">
          <span className="grad-text-3">Likely</span> to appear on your exam
        </h3>
      </div>
      <div className="space-y-3 max-w-3xl">
        {items.map((q, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            data-testid={`exam-q-${i}`}
            className="surface p-5"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#7C5CFF]/30 to-[#5AC8FA]/15 text-[#B8A4FF] flex items-center justify-center text-[12px] font-bold shrink-0 border border-[#7C5CFF]/30">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="chip chip-pink">{q.type?.replace("_", " ")}</span>
                  <span className="chip chip-orange">{q.likely_marks} marks</span>
                </div>
                <p className="text-[15px] leading-snug font-medium">{q.question}</p>
                <button
                  onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}
                  data-testid={`toggle-approach-${i}`}
                  className="link-arrow mt-3"
                >
                  {open[i] ? "Hide approach" : "Show approach"}
                </button>
                <AnimatePresence>
                  {open[i] && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 pl-3 border-l-2 border-[#7C5CFF]/40">
                        {q.key_concepts?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {q.key_concepts.map((c) => <span key={c} className="chip chip-pink">{c}</span>)}
                          </div>
                        )}
                        <p className="text-[14px] text-[#B0AEBC] leading-relaxed">{q.answer_approach}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
